import { useEffect, useState } from 'react';
import { resolveDownloadUrl } from '../../services/storageService';

export interface FinishedFilm {
  /** A playable URL for the finished cut, once resolved. */
  url: string | null;
  /** The cut exists but its URL could not be resolved — the creator must be told, not left guessing. */
  failed: boolean;
}

/**
 * THE FINISHED CUT'S PLAYABLE URL, resolved from its Storage path (BUGS.md 2026-09-27).
 *
 * The Studio used to resolve this inside the render poll, guarded by the poll's own `alive` flag. The same tick
 * that learned the render was ready also moved `status` off `generating`, which tore the poll down — so the flag
 * was already false when the URL came back, and the film was dropped on the floor every time. The page then said
 * "Render the episode first" about an episode it had just rendered, until a reload.
 *
 * Keyed on the PATH alone, so it outlives whatever effect discovered the path; and a failure is reported rather
 * than swallowed, because a silent miss is indistinguishable from "there is no film".
 */
export function useFinishedFilm(path: string | null): FinishedFilm {
  const [film, setFilm] = useState<FinishedFilm>({ url: null, failed: false });
  useEffect(() => {
    setFilm({ url: null, failed: false });
    if (!path) return undefined;
    let alive = true;
    resolveDownloadUrl(path)
      .then((url) => { if (alive) setFilm({ url, failed: false }); })
      .catch(() => { if (alive) setFilm({ url: null, failed: true }); });
    return () => { alive = false; };
  }, [path]);
  return film;
}
